/**
 * Currency Formatting Utility
 * Formats numbers as currency with proper formatting
 */

/**
 * Format a number as USD currency
 *
 * @param amount - The amount to format (in dollars, not cents)
 * @returns Formatted currency string (e.g., "$1,234.56")
 *
 * @example
 * formatCurrency(1234.56) // Returns "$1,234.56"
 * formatCurrency(0) // Returns "$0.00"
 * formatCurrency(-50) // Returns "-$50.00"
 */
export function formatCurrency(amount: number): string {
  // Handle edge cases
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '$0.00';
  }

  // Handle negative numbers
  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount);

  // Format the number with 2 decimal places and thousands separators
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absoluteAmount);

  // Return with negative sign outside dollar sign if negative
  return isNegative ? `-${formatted}` : formatted;
}

/**
 * Format cents as currency
 *
 * @param cents - The amount in cents
 * @returns Formatted currency string
 *
 * @example
 * formatCentsAsCurrency(12345) // Returns "$123.45"
 */
export function formatCentsAsCurrency(cents: number): string {
  return formatCurrency(cents / 100);
}

/**
 * Parse currency string to number
 *
 * @param currencyString - Currency string to parse
 * @returns Numeric value
 *
 * @example
 * parseCurrency("$1,234.56") // Returns 1234.56
 */
export function parseCurrency(currencyString: string): number {
  // Remove all non-numeric characters except decimal point and minus sign
  const cleaned = currencyString.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);

  return isNaN(parsed) ? 0 : parsed;
}
